/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Loader from '../../../../component/common/Loader';
import SearchInput from '../../../../component/common/Input/SearchInput';
import Table from '../../../../component/common/Table';
import Pagination from '../../../../component/common/table2/Pagination';
import { useCompanySectionFilter } from '../../../../component/B2B/CompanySectionLayout';
import { SidebarContext } from '../../../../context/SidebarContext';
import { getLocalStorageItem } from '../../../../utils/helper';
import { fetchCompanyChallengesUsers } from './companyChallengesApi';
import { TableCard } from '../../../../component/common/designSystem';

const PER_PAGE = 10;

/**
 * Company Challenges → Employees/Members list.
 *
 * EVERY user of the current population appears, including the ones who never
 * joined a challenge — the gap between "could take part" and "did" is the
 * point of the page, so never-joined rows show dashes rather than being
 * filtered out.
 *
 * A row opens that person's challenge profile.
 */
const CChallengesUsers = () => {
  const navigate = useNavigate();
  const { isB2BFansMode } = useContext(SidebarContext);
  const { department } = useCompanySectionFilter();
  const userData = JSON.parse(getLocalStorageItem('userData') || '{}');
  const slug = userData?.slug;

  const [loader, setLoader] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalParticipants, setTotalParticipants] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  // The tracker the challenges read names the column — "Steps" today.
  const [volumeLabel, setVolumeLabel] = useState('Steps');

  const population = isB2BFansMode ? 'members' : 'employees';

  const fetchData = useCallback(
    async (page, search) => {
      setLoader(true);
      const response = await fetchCompanyChallengesUsers({
        page,
        perPage: PER_PAGE,
        search,
        department
      });
      setRows(
        (response.data || []).map((row) => ({
          ...row,
          // Never-joined users get dashes, not a wall of zeroes.
          challengesJoinedText: row.challengesJoined > 0 ? `${row.challengesJoined}` : '-',
          challengesCompletedText: row.challengesJoined > 0 ? `${row.challengesCompleted}` : '-',
          volumeText: row.volume > 0 ? row.volume.toLocaleString() : '-',
          activeDaysText: row.volume > 0 ? `${row.activeDays} days` : '-',
          rankText: row.rank ? `#${row.rank}` : '-'
        }))
      );
      setTotalCount(response.totalCount || 0);
      setTotalParticipants(response.totalParticipants || 0);
      if (response.primaryDataTypeLabel) setVolumeLabel(response.primaryDataTypeLabel);
      setLoader(false);
    },
    [department, isB2BFansMode]
  );

  // Debounced fetch on search / page / scope change.
  useEffect(() => {
    const timeout = setTimeout(() => fetchData(currentPage, searchTerm), 200);
    return () => clearTimeout(timeout);
  }, [fetchData, searchTerm, currentPage]);

  useEffect(() => setCurrentPage(1), [department, isB2BFansMode]);

  const columns = [
    { title: 'Name', key: 'user_profile', sortable: false, align: 'left', type: 'profile' },
    { title: 'Rank', key: 'rankText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Joined', key: 'challengesJoinedText', sortable: false, align: 'center', type: 'text3' },
    {
      title: 'Completed',
      key: 'challengesCompletedText',
      sortable: false,
      align: 'center',
      type: 'text3'
    },
    { title: volumeLabel, key: 'volumeText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Active Days', key: 'activeDaysText', sortable: false, align: 'center', type: 'text3' },
    {
      title: '',
      key: 'action',
      align: 'right',
      type: 'action',
      arrowClickFun: (rowData) =>
        navigate(`/${slug}/challenges/insights/user?userId=${rowData?._id}`)
    }
  ];

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Challenges — {isB2BFansMode ? 'Members' : 'Employees'} | Shoorah</title>
      </Helmet>
      {loader ? <Loader /> : null}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {totalParticipants.toLocaleString()} of {totalCount.toLocaleString()} {population} have
          joined a challenge. Open a row to see their full challenge profile.
        </p>
        <SearchInput
          id="company-challenges-users-search"
          name="company-challenges-users-search"
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setCurrentPage(1);
            setSearchTerm(e.target.value);
          }}
          placeholder="Search users"
          className="w-full sm:w-auto px-3 py-2"
        />
      </div>

      <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: PER_PAGE,
            onPageChange: (page) => setCurrentPage(page)
          }}
        >
          <Table
          columns={columns}
          data={rows}
          name="company_challenges_users"
          bottomBorder={totalCount > PER_PAGE}
          loader={loader}
        />
        </TableCard>

      
    </div>
  );
};

export default CChallengesUsers;
